import { uploadPrivateFile } from '@/lib/api/services/storage.service';
import { createApplicationApi } from '@/modules/service-desk/shared/application-api';
import { runServiceDeskWorkflow } from '@/modules/service-desk/shared/application-workflows';
import { ServiceDeskRuleError } from '@/modules/service-desk/shared/service-desk.types';
import { requiresPayment, validateImageFile } from '../utils/id-card-rules';
import { IdCardApplyValues } from '../schemas/id-card-apply-form-schema';
import { IdCardApplication, IdCardStatus } from '../types/id-card.types';
import { ID_CARD_FIELDS } from '../graphql/id-card.fields';

export const idCardApi = createApplicationApi<IdCardApplication>(
  'IdCardApplication',
  ID_CARD_FIELDS,
  { deliveredLast: true }
);

interface SubmitIdCardParams {
  existing?: IdCardApplication;
  values: IdCardApplyValues;
  /** A newly chosen photo; when omitted the application keeps its existing one. */
  photoFile: File | null;
  keepExistingPhoto: boolean;
  receiptFile: File | null;
  keepExistingReceipt: boolean;
}

/**
 * Uploads `file` to Blocks storage as a Private file (the photo and the payment receipt are personal
 * data, read back through short-lived signed URLs) and returns its id, or keeps `existingId` when
 * nothing new was chosen.
 */
const resolveFileId = async (
  file: File | null,
  existingId: string | undefined,
  keepExisting: boolean
) => {
  if (file) {
    const invalidReason = validateImageFile(file);
    if (invalidReason) throw new ServiceDeskRuleError(invalidReason);
    const { fileId } = await uploadPrivateFile(file);
    if (!fileId) throw new ServiceDeskRuleError('UPLOAD_FAILED');
    return fileId;
  }
  return keepExisting ? (existingId ?? '') : '';
};

/**
 * Legacy `CreateIdCardApplicationCommand`, now the ID_CARD_APPLICATION_SUBMIT workflow. The photo is
 * mandatory; a payment method, reference and receipt belong to a Reissue only, so a New Joiner
 * application clears them (the workflow enforces both).
 */
export const submitIdCardApplication = async ({
  existing,
  values,
  photoFile,
  keepExistingPhoto,
  receiptFile,
  keepExistingReceipt,
}: SubmitIdCardParams) => {
  const photoId = await resolveFileId(photoFile, existing?.PhotoId, keepExistingPhoto);
  if (!photoId) throw new ServiceDeskRuleError('PHOTO_IS_REQUIRED');

  const paid = requiresPayment(values.applicationType);
  const paymentReferenceId = paid
    ? await resolveFileId(receiptFile, existing?.PaymentReferenceId, keepExistingReceipt)
    : '';

  return runServiceDeskWorkflow('ID_CARD_APPLICATION_SUBMIT', {
    itemId: existing?.ItemId,
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    designation: values.designation.trim(),
    email: values.email.trim(),
    mobileNumber: values.mobileNumber.trim(),
    bloodGroup: values.bloodGroup,
    employeeId: values.employeeId.trim(),
    photoId,
    applicationType: values.applicationType,
    paymentMethod: paid ? (values.paymentMethod ?? '') : '',
    paymentReference: paid ? (values.paymentReference ?? '').trim() : '',
    paymentReferenceId,
  });
};

interface ChangeIdCardStatusParams {
  itemId: string;
  status: IdCardStatus;
}

/** Legacy `UpdateIdCardApplicationStatusCommand` (ID_CARD_APPLICATION_STATUS workflow). */
export const changeIdCardStatus = ({ itemId, status }: ChangeIdCardStatusParams) =>
  runServiceDeskWorkflow('ID_CARD_APPLICATION_STATUS', { itemId, status });
